/**
 * Pure nav-gating config + filter. Kept free of react-router / MUI imports so it
 * is unit-testable without rendering (the view layer trips the jsdom TextEncoder
 * flake on Windows).
 *
 * Two gates, keyed off the build's stack:
 *  - HIDDEN_FOR_BROKER: platform/internal pages hidden on the broker-admin build.
 *  - SELF_SERVE_ONLY_PATHS: self-serve-billing pages hidden on the sales-led
 *    (Intelligent Automation) build. The Conversion Funnel is a sign-up -> paid
 *    journey; IA is sales-provisioned (no trials/subscriptions), so the funnel is
 *    structurally all-zeros there — show it only on the self-serve (broker) build.
 */

export const HIDDEN_FOR_BROKER = ['/ndas', '/agreements', '/aws-health'];
// Referral Sources (Trello #260) is broker-AI distribution-partner reporting —
// shown only on the self-serve (broker) build, like the Conversion Funnel.
// Broker AI Adoption (cross-stack Titan-enablement report) is a Broker-AI product
// concern surfaced only on the broker-admin build, like the Conversion Funnel.
// Subscription Movements (Trello #365) reports cancellations/downgrades/upgrades
// of self-serve Stitch subscriptions — a broker-AI-only concept, so broker build
// only (the tab would be structurally empty on the sales-led IA build).
// EFT Payments was in this list while EFT was a broker-AI-only channel. Removed
// by Trello #664: Intelligent Automation runs the same manual-EFT flow, and the
// backend scopes the queue to the calling admin's own stack, so the page is
// correct — and necessary — on both builds.
export const SELF_SERVE_ONLY_PATHS = ['/conversion-funnel', '/referral-sources', '/conversion-requests', '/broker-adoption', '/subscription-movements'];

/**
 * Short, to-the-point tooltip copy for each sidebar nav item, keyed by route.
 * One concise line per item (kept <= 90 chars) — surfaced on hover in Layout.tsx
 * so the label's purpose is obvious without opening the page. This is deliberately
 * terser than the sales-oriented multi-sentence PAGE_HELP copy in
 * ./page-help-content.ts (that renders the in-page help panel); a nav tooltip
 * needs a glanceable one-liner, and it must cover every nav item, not just the
 * usage/health pages. Every path in Layout.tsx's menuItems must have an entry here
 * (the nav-config.test.ts "tooltip for every nav path" test enforces it).
 */
export const NAV_ITEM_DESCRIPTIONS: Record<string, string> = {
  '/': 'Platform overview — cost, usage and health at a glance',
  '/users': 'Browse, search and manage all user accounts',
  '/organizations': 'Manage organisations, their plans and settings',
  '/api-keys': 'Issue and revoke API keys for programmatic access',
  '/ndas': 'Non-disclosure agreements on file',
  '/agreements': 'Service agreements on file',
  '/features': 'Turn platform features on or off per organisation',
  '/notifications': 'Send and review in-app user notifications',
  '/feedback': 'End-of-trial star ratings and reviews from users',
  '/whats-new': 'Publish product-update announcements to users',
  '/system-flows': 'Plain-English diagrams of how the system works',
  '/cost-report': 'AI spend per stack and organisation, by month',
  '/usage-health': "Each org's monthly run allowance — spot orgs near their cap",
  '/per-user-usage': 'Run consumption per user, highest first',
  '/most-used-tools': 'Which tools brokers run most this month',
  '/inactive-users': "Users who haven't run a tool lately — churn risk",
  '/leaderboard': 'Most-active users and organisations by runs',
  '/broker-adoption': 'Orgs with the Broker AI (Titan) models enabled',
  '/conversion-funnel': 'Sign-up to paid drop-off across the journey',
  '/referral-sources': 'Where orgs came from and how each source converts',
  '/eft-payments': 'Review queue for bank-transfer subscription payments',
  '/subscription-movements': 'Who cancelled, downgraded or upgraded — and why',
  '/conversion-requests': 'Team-account requests from capped trial users',
  '/aws-health': 'Health of the underlying AWS infrastructure',
  // Visible on BOTH builds: invoicing is a shared concern (the per-org invoice
  // panel on the org detail page is ungated too), so this is neither
  // broker-hidden nor self-serve-only. (Trello #363)
  '/bulk-invoices': 'Generate or download invoices for many orgs and months at once',
  '/export': 'Download platform data as a spreadsheet',
};

export function getVisibleMenuItems<T extends { path: string }>(
  items: T[],
  opts: { isBrokerAdmin: boolean },
): T[] {
  return items.filter((item) =>
    opts.isBrokerAdmin
      ? !HIDDEN_FOR_BROKER.includes(item.path)
      : !SELF_SERVE_ONLY_PATHS.includes(item.path),
  );
}
